import { useRef, useState } from "react";
import {
    Box,
    Typography,
    Popper,
    IconButton,
} from "@mui/material";

import Iconify from "src/components/iconify";

const ActivationCell = ({
    params,
    setConfirmDisable,
    setModalCreateStrategy,
}: any) => {

    const VALID_MODE = "KO Bidding Manual";

    const details = params.row.activations_details ?? [];

    const activeDetails = details.filter(
        (d: any) => d.status === true && d.mode?.startsWith(VALID_MODE)
    );

    const hasAnyMode = activeDetails.length > 0;

    const hasManualStrategy = activeDetails.some(
        (d: any) => d.mode?.startsWith("KO Bidding Manual")
    );

    const canCreateStrategy = !hasManualStrategy;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [activeDetail, setActiveDetail] = useState<any>(null);

    const timeoutRef = useRef<any>(null);

    const handleEnter = (e: React.MouseEvent<HTMLElement>, detail: any) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        setAnchorEl(e.currentTarget);
        setActiveDetail(detail);
    };

    const handleLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
            setActiveDetail(null);
        }, 120);
    };

    const open = Boolean(anchorEl);

    const modeLabel = "Manual";

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                overflow: "hidden",
                px: 1,
            }}
        >
            {hasAnyMode ? (
                activeDetails.map((detail: any, i: number) => (
                    <Typography
                        key={i}
                        onMouseEnter={(e) => handleEnter(e, detail)}
                        onMouseLeave={handleLeave}
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            py: 0.4,
                            px: 0.5,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            borderRadius: "6px",
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: "pointer",
                            color: "rgba(48, 57, 176, 1)",
                            background: "rgba(111, 120, 247, 0.19)",
                        }}
                    >
                        <Iconify icon="mingcute:pencil-3-line" width={16} />

                        <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                            Manual
                        </Box>
                    </Typography>
                ))
            ) : (
                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                    --
                </Typography>
            )}

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="top"
                style={{ zIndex: 2000 }}
                modifiers={[
                    {
                        name: "offset",
                        options: { offset: [0, 8] },
                    },
                ]}
            >
                <Box
                    onMouseEnter={() => {
                        if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    }}

                    onMouseLeave={handleLeave}
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        p: 1,
                        background: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.2,
                            py: 0.6,
                            borderRadius: "8px",
                            background: "rgba(111, 120, 247, 0.19)",
                            color: "rgba(48, 57, 176, 1)",
                        }}
                    >
                        <Iconify icon="mingcute:pencil-3-line" width={16} />

                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                            {modeLabel}
                        </Typography>
                    </Box>

                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.stopPropagation();

                            setConfirmDisable({
                                open: true,
                                row: params.row,
                                mode: "KO Bidding Manual",
                            });

                            setAnchorEl(null);
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </IconButton>
                </Box>
            </Popper>

            {canCreateStrategy && (
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        setModalCreateStrategy({
                            open: true,
                            row: [params.row],
                        });
                    }}
                    sx={{
                        position: "absolute",
                        right: 6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 22,
                        height: 22,
                        borderRadius: "6px",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        opacity: 0,
                        transition: "all 0.2s ease",
                        ".MuiDataGrid-cell:hover &": {
                            opacity: 1,
                        },
                    }}
                >
                    <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                        +
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ActivationCell;